import { PlatformModule } from '@angular/cdk/platform';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule, forwardRef } from '@angular/core';
import { TitleStrategy } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { MAT_LUXON_DATE_ADAPTER_OPTIONS, MAT_LUXON_DATE_FORMATS } from '@angular/material-luxon-adapter';
import { QuillModule } from 'ngx-quill';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ApiModule } from 'src/_api';
import { environment } from 'src/environments/environment';
import { ChannelComponent } from './admin/channel/channel.component';
import { ChannelCreateComponent } from './admin/channel/create/channel-create.component';
import { ClientCreateComponent } from './admin/client/client-create/client-create.component';
import { ClientComponent } from './admin/client/client.component';
import { CustomProgressBarComponent } from './admin/custom-progress-bar/custom-progress-bar.component';
import { CustomerCreateComponent } from './admin/customer/create/customer-create.component';
import { CustomerComponent } from './admin/customer/customer.component';
import { ContactPersonCreateModal } from './admin/customer/person-create/contact-person-create.modal';
import { CategoryComponent } from './admin/person-of-interest/category/category/category.component';
import { CreateCategoryComponent } from './admin/person-of-interest/category/create-category/create-category.component';
import { DeleteCategoryWarningModalComponent } from './admin/person-of-interest/category/delete-category-warning-modal/delete-category-warning-modal.component';
import { PersonOfInterestCreateComponent } from './admin/person-of-interest/create/person-of-interest-create.component';
import { PersonOfInterestComponent } from './admin/person-of-interest/person-of-interest.component';
import { PoiFiltersModalComponent } from './admin/person-of-interest/poi-filters-modal/poi-filters-modal.component';
import { PercentageChangePipe } from './admin/shared/percentage-change.pipe';
import { SmsChartsComponent } from './admin/sms/sms-charts/sms-charts.component';
import { SmsComponent } from './admin/sms/sms.component';
import { StartComponent } from './admin/start/start.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ApiInterceptor } from './core/api.interceptor';
import { AuthService } from './core/auth/auth.service';
import { ConfirmEmailComponent } from './core/auth/confirm-email/confirm-email.component';
import { HangfireHandoffComponent } from './core/auth/hangfire-handoff/hangfire-handoff.component';
import { LoginComponent } from './core/auth/login/login.component';
import { RegisterComponent } from './core/auth/register/register.component';
import { LoaderComponent } from './core/loader/loader.component';
import { NavSettingsComponent } from './core/nav-settings/nav-settings.component';
import { SidenavComponent } from './core/sidenav/sidenav.component';
import { BaseInputDirective } from './shared/base-input.directive';
import { ClickOutsideDirective } from './shared/click-outside.directive';
import { ConfirmationModalComponent } from './shared/confirmation-modal/confirmation-modal.component';
import { CustomSnackbarComponent } from './shared/custom-snackbar/custom-snackbar.component';
import { GenericDialogComponent } from './shared/generic-dialog/generic-dialog.component';
import { DropdownInputComponent } from './shared/dropdown-input/dropdown-input.component';
import { FormFieldGroupComponent } from './shared/form-field-group/form-field-group.component';
import { LanguageModalComponent } from './shared/language-modal/language-modal.component';
import { MoveFileToFolderModalComponent } from './shared/move-file-to-folder-modal/move-file-to-folder-modal.component';
import { MultiSelectChipsComponent } from './shared/multi-select-chips/multi-select-chips.component';
import { PasswordResetConfirmModalComponent } from './shared/password-reset-confirm-modal/password-reset-confirm-modal.component';
import { IsRemovedAdvertisementPipe } from './shared/pipes/is-removed-advertisement.pipe';
import { RangeSliderComponent } from './shared/range-slider/range-slider.component';
import { RangeDirective } from './shared/range.directive';
import { SharedModule } from './shared/shared.module';
import { SurveyDialogComponent } from './shared/survey-dialog/survey-dialog.component';
import { TableComponent } from './shared/table/table.component';
import { TriggerMediaModal } from './shared/trigger-media-model/trigger-media-model.component';
import { ViewMediaModal } from './shared/view-media-modal/view-media.modal';
import { ChooseTenantComponent } from './core/auth/choose-tenant/choose-tenant.component';
import { PublicLayotComponent } from './layout/public-layot/public-layot.component';
import { PrivateLayotComponent } from './layout/private-layot/private-layot.component';
import { MediaUploadExternalComponent } from '@admin/media-upload-external/media-upload-external.component';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { storeEffects, storeReducers } from './store/store.config';
import { SidenavItemComponent } from './core/sidenav/sidenav-item/sidenav-item.component';
import { ReportsComponent } from './admin/statistics/reports/reports.component';
import { ReportsDataComponent } from './admin/statistics/reports/reports-data/reports-data.component';
import { CommonModule, DatePipe } from '@angular/common';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { getPaginatorIntl } from './shared/utils/paginator-intl.factory';
import { CustomerFilterSectionComponent } from './admin/customer/customer-filter-section/customer-filter-section.component';
import { ChannelFilterSectionComponent } from './admin/channel/channel-filter-section/channel-filter-section.component';
import { LuxonLanguageCodes } from './core/translations';
import { CustomTitleStrategyService } from './core/services/custom-title-strategy.service';
import { BannerComponent } from './shared/components/banner/banner.component';

export function rootLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '../assets/i18n/', '.json');
}
@NgModule({
  declarations: [
    LoaderComponent,
    LoginComponent,
    RegisterComponent,
    ConfirmEmailComponent,
    HangfireHandoffComponent,
    SidenavComponent,
    AppComponent,
    StartComponent,
    ChannelComponent,
    ChannelCreateComponent,
    MultiSelectChipsComponent,
    CustomerComponent,
    CustomerCreateComponent,
    FormFieldGroupComponent,
    PersonOfInterestComponent,
    PersonOfInterestCreateComponent,
    ContactPersonCreateModal,
    ViewMediaModal,
    ConfirmationModalComponent,
    PasswordResetConfirmModalComponent,
    LanguageModalComponent,
    CustomProgressBarComponent,
    CustomSnackbarComponent,
    MoveFileToFolderModalComponent,
    CategoryComponent,
    CreateCategoryComponent,
    DeleteCategoryWarningModalComponent,
    SmsComponent,
    SmsChartsComponent,
    ClientComponent,
    ClientCreateComponent,
    TableComponent,
    TriggerMediaModal,
    RangeDirective,
    RangeSliderComponent,
    NavSettingsComponent,
    ClickOutsideDirective,
    PoiFiltersModalComponent,
    BaseInputDirective,
    DropdownInputComponent,
    IsRemovedAdvertisementPipe,
    PercentageChangePipe,
    ChooseTenantComponent,
    GenericDialogComponent,
    PublicLayotComponent,
    PrivateLayotComponent,
    MediaUploadExternalComponent,
    SidenavItemComponent,
    ReportsComponent, // After POI is refactored to use modules, remove this
    ReportsDataComponent, 
    CustomerFilterSectionComponent,
    ChannelFilterSectionComponent,
    BannerComponent,
    SurveyDialogComponent
  ],
  imports: [
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    PlatformModule,
    ReactiveFormsModule,
    HttpClientModule,
    ApiModule.forRoot({ rootUrl: environment.apiBaseUrl }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: rootLoaderFactory,
        deps: [HttpClient],
      },
    }),
    SharedModule,
    StoreModule.forRoot(storeReducers),
    EffectsModule.forRoot(storeEffects),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      autoPause: true,
    }),
    QuillModule.forRoot()
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: forwardRef(() => ApiInterceptor),
      multi: true,
      deps: [AuthService, MatSnackBar],
    },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { 
      appearance: 'outline',
      floatLabel: 'always',
      subscriptSizing: 'dynamic'
    }},
    { provide: MAT_LUXON_DATE_ADAPTER_OPTIONS, useValue: { useUtc: false, firstDayOfWeek: 1 } },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        ...MAT_LUXON_DATE_FORMATS,
        parse: {
          ...MAT_LUXON_DATE_FORMATS.parse,
          dateInput: 'yyyy-MM-dd',
        },
        display: {
          ...MAT_LUXON_DATE_FORMATS.display,
          dateInput: 'yyyy-MM-dd',
        },
      },
    },
    {
      provide: MatPaginatorIntl,
      useFactory: getPaginatorIntl,
      deps: [TranslateService]
    },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: () => {
        const locale = localStorage.getItem('language');
        return LuxonLanguageCodes[locale] || LuxonLanguageCodes['en'];
      },
    },
    { provide: TitleStrategy, useClass: CustomTitleStrategyService },
    DatePipe
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
